import React, { useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { useForm } from "react-hook-form"
import { TslNewsletterService } from "services/api-workers-service"
import type { TslNewsletterSourceCreatePayload } from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import { Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import Switch from "ui/components/atoms/switch"
import { P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import Modal from "ui/components/molecules/modal"

import { TSL_LANGUAGES } from "./data"

type FormValues = TslNewsletterSourceCreatePayload

const SourceCreateModal = ({ show, setShow }: { show: boolean; setShow: (e: boolean) => void }) => {
  const queryClient = useQueryClient()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      domain: "",
      language: "pl",
      active: true,
      extract_events: true,
      source_url: "",
    },
  })

  const [loading, setLoading] = useState(false)
  const [generalError, setGeneralError] = useState<string | null>(null)

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setLoading(true)
      setGeneralError(null)
      try {
        const sourceUrl = values.source_url?.trim()
        await TslNewsletterService.createSource({
          name: values.name.trim(),
          domain: values.domain.trim().toLowerCase(),
          language: values.language,
          active: values.active,
          extract_events: values.extract_events,
          source_url: sourceUrl ? sourceUrl : undefined,
        })
        await queryClient.invalidateQueries({ queryKey: ["tsl", "sources"] })
        reset()
        setShow(false)
      } catch (e) {
        SentryService.error("[ERROR TslNewsletterService.createSource]:", e)
        if (axios.isAxiosError(e) && e.response?.status === 409) {
          setError("domain", {
            type: "manual",
            message: "Źródło z tą domeną już istnieje.",
          })
        } else if (axios.isAxiosError(e) && e.response?.status === 502) {
          setGeneralError("Nie udało się utworzyć kanału w rss.app. Spróbuj ponownie.")
        } else if (axios.isAxiosError(e) && e.response?.status === 503) {
          setGeneralError(
            "Backend nie skonfigurowany dla tego języka. Skontaktuj się z administratorem.",
          )
        } else {
          const detail =
            (axios.isAxiosError(e) && (e.response?.data as any)?.detail) ||
            "Nie udało się dodać źródła. Spróbuj ponownie."
          setGeneralError(detail)
        }
      } finally {
        setLoading(false)
      }
    },
    [queryClient, reset, setError, setShow],
  )

  return (
    <Modal
      show={show}
      setShow={setShow}
      type="center"
      title="Dodaj źródło"
      text="Wprowadź dane wydawcy do prasówki TSL"
      $maxWidth="500px"
    >
      <>
        {generalError && (
          <Alert $type="error" $small $marginBottom="S">
            {generalError}
          </Alert>
        )}
        <Input
          name="name"
          label="Nazwa"
          placeholder="np. Trans.info"
          error={errors.name}
          value={watch("name")}
          rhf={register("name", {
            required: "Podaj nazwę źródła",
            minLength: { value: 1, message: "Podaj nazwę źródła" },
          })}
          $marginBottom="S"
        />
        <Input
          name="domain"
          label="Domena"
          placeholder="np. trans.info"
          error={errors.domain}
          value={watch("domain")}
          rhf={register("domain", {
            required: "Podaj domenę",
            pattern: {
              value: /^[a-z0-9.-]+\.[a-z]{2,}$/i,
              message: "Niepoprawny format domeny",
            },
          })}
          $marginBottom="S"
        />
        <Select
          name="language"
          label="Język"
          options={TSL_LANGUAGES}
          value={watch("language")}
          onChange={({ value }) => setValue("language", value)}
          $marginBottom="S"
        />
        <Input
          name="source_url"
          label="URL źródła (opcjonalne)"
          placeholder={watch("domain") ? `https://${watch("domain")}` : "https://trans.info/pl/"}
          value={watch("source_url") ?? ""}
          rhf={register("source_url")}
          $marginBottom="XXS"
        />
        <P $small $color="third" $marginBottom="S">
          URL strony do crawlowania w rss.app. Domyślnie strona główna domeny, możesz podać
          konkretną sekcję np. https://trans.info/pl/news/
        </P>
        <Row $alignItems="center" $marginBottom="XS">
          <Switch
            name="active"
            value={watch("active")}
            onChange={({ value }) => setValue("active", value)}
          />
          <span style={{ marginLeft: 8 }}>Aktywne</span>
        </Row>
        <Row $alignItems="center" $marginBottom="XXS">
          <Switch
            name="extract_events"
            value={watch("extract_events") ?? true}
            onChange={({ value }) => setValue("extract_events", value)}
          />
          <span style={{ marginLeft: 8 }}>Ekstrahuj eventy z artykułów</span>
        </Row>
        <P $small $color="third" $marginBottom="M">
          Gdy wyłączone, eventy znalezione w artykułach z tego źródła będą ignorowane przy zapisie.
        </P>
        <Row $justifyContent="flex-end">
          <Button
            $type="secondary"
            onClick={() => setShow(false)}
            $marginRight="XS"
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button icon="plus" loading={loading} onClick={handleSubmit(onSubmit)}>
            Dodaj źródło
          </Button>
        </Row>
      </>
    </Modal>
  )
}

export default SourceCreateModal
